// this file is used to generate example data for the analytics page
import type { DashboardQuery } from "~/features/dashboard/query.generated";
import { dateToInteger, TimeInMilliseconds } from "./utils.ts";

// Helper function to convert YYYYMMDD integer to milliseconds timestamp (start of day UTC)
function dateIntegerToTimestamp(dateInt: number): number {
	const dateStr = dateInt.toString();
	const year = parseInt(dateStr.substring(0, 4), 10);
	const month = parseInt(dateStr.substring(4, 6), 10) - 1; // JS month is 0-indexed
	const day = parseInt(dateStr.substring(6, 8), 10);
	// Return timestamp for the start of the day in UTC
	return new Date(Date.UTC(year, month, day)).getTime();
}

// Available countries with their codes - this can be kept for generation logic but won't be part of the final type
type CountryCode = "US" | "GB" | "AU" | "ES" | "IL";

// Define types based on DashboardQuery for better maintainability
type DailyAnalyticsNewUserInput =
	DashboardQuery["analytics"][number]["newUsers"][number];
type DailyAnalyticsSavedProjectInput =
	DashboardQuery["analytics"][number]["savedProjects"][number];
type DailyAnalyticsCartProjectInput =
	DashboardQuery["analytics"][number]["cartProjects"][number];
type DailyAnalyticsOrderedProjectInput =
	DashboardQuery["analytics"][number]["orderedProjects"][number];
type DailyAnalyticsOutput = DashboardQuery["analytics"][number];

// Generate analytics data for a specific date
function generateDailyAnalytics(
	date: number,
	countryDistribution: Record<CountryCode, number>,
): Omit<DailyAnalyticsOutput, "id"> {
	// id will be added when mapping in generateExampleData
	const newUsers: DailyAnalyticsNewUserInput[] = [];
	const savedProjects: DailyAnalyticsSavedProjectInput[] = [];
	const cartProjects: DailyAnalyticsCartProjectInput[] = [];
	const orderedProjects: DailyAnalyticsOrderedProjectInput[] = [];
	let userIdCounter = 1;
	const dailyTimestamp = dateIntegerToTimestamp(date);
	const dailyTimestampISO = new Date(dailyTimestamp).toISOString();

	// Generate users for each country based on distribution
	for (const [countryCode, count] of Object.entries(countryDistribution)) {
		for (let i = 0; i < count; i++) {
			newUsers.push({
				id: `user-${userIdCounter}`,
				emailAddress: `user${userIdCounter}@example.com`,
				joinedAt: new Date(
					dailyTimestamp + Math.floor(Math.random() * TimeInMilliseconds.DAY),
				).toISOString(),
				ipAddressCountry: countryCode, // Removed unnecessary cast
				__typename: "DailyanalyticsNewusers",
			});
			userIdCounter = userIdCounter + 1;
		}
	}

	// Generate saved projects based on new users for the day
	const totalNewUsers = newUsers.length;
	const numSavedProjects = Math.floor(
		totalNewUsers * (0.2 + Math.random() * 0.3), // 20% to 50% of new users save a project
	);
	let projectIdCounter = 1;
	for (let i = 0; i < numSavedProjects; i++) {
		const userIndex = Math.floor(Math.random() * totalNewUsers);
		const user = newUsers[userIndex];

		if (user) {
			const projectBase = {
				id: `proj-${date}-${projectIdCounter}`,
				userId: user.id,
				createdAt: dailyTimestampISO, // Use ISO string
				mediumImage: null, // Use null as per generated type
			};

			savedProjects.push({
				...projectBase,
				__typename: "DailyanalyticsSavedprojects",
			});

			// Add some saved projects to cart (about 60-80% of saved projects)
			if (Math.random() < 0.7) {
				const cartProject = {
					...projectBase,
					__typename: "DailyanalyticsCartprojects" as const,
				};
				cartProjects.push(cartProject);

				// Add some cart projects to ordered (about 50-70% of cart projects)
				if (Math.random() < 0.6) {
					orderedProjects.push({
						...projectBase,
						__typename: "DailyanalyticsOrderedprojects" as const,
					});
				}
			}
			projectIdCounter += 1;
		}
	}

	return {
		newUsers,
		savedProjects,
		cartProjects,
		orderedProjects,
		__typename: "DailyAnalytics" as const,
	};
}

// Generate the complete example data
export function generateExampleData({
	fromDate,
	toDate,
}: {
	fromDate: Date;
	toDate: Date;
}): DashboardQuery {
	// use date-fns to calculate the amount of days between the two dates
	const amountOfDays =
		(toDate.getTime() - fromDate.getTime()) / TimeInMilliseconds.DAY + 1;

	const fromDateInteger = dateToInteger(fromDate);

	// Define date range
	const dates = Array.from(
		{ length: amountOfDays },
		(_, i) => fromDateInteger + i,
	);

	// Function to generate consistent distribution for a given day
	const generateDistribution = (
		dayIndex: number,
	): Record<CountryCode, number> => {
		// Use a deterministic seed based on the day index
		const seed = dayIndex * 12345;

		// Create a deterministic random number generator
		const random = (min: number, max: number) => {
			const x = Math.sin(seed + dayIndex) * 10000;
			return Math.floor((x - Math.floor(x)) * (max - min + 1)) + min;
		};

		// Generate a more realistic daily user count
		// Base growth starts at 40 and increases by 0.5 users per day
		const baseGrowth = 40 + dayIndex * 0.5;

		// Add natural variation using multiple sine waves with different frequencies
		// This creates more organic-looking variation
		const variation =
			Math.sin(seed * 0.05) * 10 + // Weekly-ish variation
			Math.sin(seed * 0.2) * 5 + // Daily variation
			Math.sin(seed * 0.01) * 15; // Monthly-ish trend

		// Combine base growth with variation and ensure it stays within bounds
		const dailyUsers = Math.min(
			80,
			Math.max(40, Math.round(baseGrowth + variation)),
		);

		// Start with the generated number of users to distribute
		let remainingUsers = dailyUsers;
		const distribution: Record<CountryCode, number> = {
			US: 0,
			GB: 0,
			AU: 0,
			ES: 0,
			IL: 0,
		};

		// Distribute users to each country
		const countriesInternal: CountryCode[] = ["US", "GB", "AU", "ES", "IL"]; // Renamed to avoid conflict
		countriesInternal.forEach((country, index) => {
			if (index === countriesInternal.length - 1) {
				// Last country gets all remaining users
				distribution[country] = remainingUsers;
			} else {
				// Distribute a random portion of remaining users
				const maxForCountry = Math.floor(remainingUsers * 0.6); // Ensure no country gets more than 60%
				const usersForCountry = random(1, maxForCountry);
				distribution[country] = usersForCountry;
				remainingUsers -= usersForCountry;
			}
		});

		return distribution;
	};

	const analyticsData: DailyAnalyticsOutput[] = dates.map((date, index) => {
		const dailyGeneratedData = generateDailyAnalytics(
			date,
			generateDistribution(index),
		);
		return {
			id: date, // id is number as per DashboardQuery['analytics'][number]['id']
			...dailyGeneratedData,
		};
	});

	const generatedDataObject: DashboardQuery = {
		analytics: analyticsData,
		__typename: "Query" as const,
	};
	return generatedDataObject;
}

// Export the generated data
// export const exampleData = generateExampleData();
